<script lang="ts">
	import FaIcon from '$lib/components/FAIcon.svelte';
	import {
		faBriefcase,
		faAddressCard,
		faLaptopCode,
		faCalendarCheck,
		faScaleBalanced,
		faHandPeace,
		faAmbulance,
		faHandsHoldingCircle,
		faMicrochip,
		faBuildingColumns,
		faMessage
	} from '@fortawesome/free-solid-svg-icons';
</script>

<section class="intro">
	<h2>C Parfitt-Ford</h2>

	<p>
		I'm an <strong>interdisciplinary professional</strong> with clinical, technical, and academic experience
		across pre-hospital emergency care, web software engineering, and human-computer interaction. Much
		as that's an unusual set of words to put together, the breadth of my experience means I'm able to
		deliver value in unusual ways!
	</p>
	<p>
		Coming from a background of computer science into urgent and emergency care isn't the traditional route into healthcare;
		I chose to switch because I'm deeply passionate about high-quality patient care. As a clinician, I'm eager to go above
		and beyond for my patients: my research, both undergraduate and postgraduate, pushes healthcare to be better for those we
		serve. I've also developed mentorship and teaching skills, both in software teams and in clinical contexts: I love sharing
		the things I've learned, and, in turn, learning from others.
	</p>
	<p>
		I've a keen and curious interest in health technology and out-of-hospital urgent and emergency care. I'm beginning to develop a
		research portfolio in these areas, alongside my clinical practice.
	</p>
</section>

<div class="cv">
	<section class="places">
		<h3><FaIcon icon={faBriefcase} /> Places I've worked</h3>
		<details>
			<summary>
				<FaIcon icon={faLaptopCode} />
				<strong>Full Stack Developer</strong>
				at Poteris: July 2023 &mdash; present
			</summary>
			<p>
				Poteris is a non-profit agency working with organisations to create positive change through
				technology, in particular in the third sector and civic technology spaces. I started as the
				only in-house software engineer, with responsibilities including building and maintaining
				systems that manage campaign data, architecting data models for the storage of
				authentication and contact information, managing the deployment of internal and external
				systems into the AWS cloud, and writing internal documentation for contracted technology
				partners.
			</p>
			<p>
				In this role, I've worked in both TypeScript and Golang, using frameworks like Nest.js and
				Next.js, as well as performing DevOps tasks across both AWS and Vercel platforms.
			</p>
		</details>
		<details>
			<summary>
				<FaIcon icon={faAddressCard} />
				<strong>Director</strong>
				at Loudspeek: February 2019 &mdash; July 2026
			</summary>
			<p>
				Loudspeek provides digital advocacy solutions to some of the UK's largest progressive change
				campaigns and charities. I founded the company, writing the codebase myself, which
				processes nearly 2 million user requests per month at peak. I have experience from this role
				developing with Ruby on Rails, PostgreSQL and Redis, among various other technologies.
			</p>
			<p>
				In terms of soft skills, I also overcame significant adversity in the process of founding
				Loudspeek because of my age, including not being able to open a bank account for the company
				for more than six months until I finally persuaded a bank to do so.
			</p>
		</details>
		<details>
			<summary>
				<FaIcon icon={faLaptopCode} />
				<strong>Computer Scientist</strong>
				at Netcraft: July to September 2022
			</summary>
			<p>
				I spent the summer between my second and third years at university working at Netcraft's
				London office as a Computer Scientist. In this role I designed and developed automated
				systems combating cybercrime, as well as exploring ways to work with both government
				agencies and upstream providers to take fraudulent content offline, protecting brands and
				consumers alike.
			</p>
		</details>
	</section>

	<section class="volunteering">
		<h3>My volunteering <FaIcon icon={faHandPeace} /></h3>
		<details>
			<summary>
				<FaIcon icon={faAmbulance} />
				<strong>Emergency Ambulance Crew</strong>
				at St John Ambulance: October 2022 &mdash; present
			</summary>
			<p>
				St John Ambulance is England's largest first aid charity, providing medical cover at events,
				in communities, and in response to emergency calls in partnership with the NHS. From 2022 to
				2025, we served as England's national Ambulance Auxiliary, meaning supporting pre-hospital
				emergency care delivery across NHS ambulance services all year round.
			</p>
			<p>
				I qualified as an Emergency Ambulance Crew with St John in October 2022. Since then, I've
				worked on double-staffed ambulances in SWAST, SCAS, IOWAS and EMAS trusts. I've also formed
				part of the medical team at a variety of significant events, ranging from Manchester and
				Brighton Prides to the Great North Run and London Marathon, working alongside other
				healthcare professionals and providing leadership and mentorship to junior members of the
				team, such as our first aiders.
			</p>
			<p>
				I regularly teach university First Aid Societies and other community groups as part of this
				role, differentiating content across an audience ranging from laypeople and schoolchildren
				to final-year medical students. I am also a qualified Cycle Responder, providing a
				fast-response capability to large events, with the ability to triage and treat patients
				without requiring a double-staffed ambulance response.
			</p>
		</details>
		<details>
			<summary>
				<FaIcon icon={faHandsHoldingCircle} />
				<strong>Trustee</strong>
				at the University of Southampton Students' Union: July 2022 &mdash; July 2023
			</summary>
			<p>
				I served as a Student Trustee of SUSU - the University of Southampton Students' Union - in
				my final year of study at the University of Southampton. In this role I was responsible for
				ensuring the Union is operating in accordance with its policies, its objects and the law.
			</p>
			<p>
				I worked with a Board composed of members with a wide range of multidisciplinary
				backgrounds, and oversaw the beginning of a broad-ranging governance transformation project.
				I sat on the Union's Audit and Risk Subcommittee of the Board, working alongside external
				auditors examining risks including financial, reputational, and safeguarding risks.
			</p>
		</details>
		<details>
			<summary>
				<FaIcon icon={faCalendarCheck} />
				<strong>Unit Events Lead</strong>
				at St John Ambulance University of Southampton: June 2021 &mdash; May 2023
			</summary>
			<p>
				St John Ambulance is England's largest first aid charity, providing medical cover at events,
				in communities, and in response to emergency calls in partnership with the NHS. Student
				Volunteering Units in St John act as regular Event Services Units, and are trained and run
				to the same standard as any other adult unit.
			</p>
			<p>
				As Unit Events Lead of the University of Southampton unit, my role was to manage the
				deployment to events of students at the university giving up their time to volunteer with St
				John, and ensuring we appropriately staffed our events. I was also responsible for the
				operational management of volunteers, together with the Unit Manager and Unit Training Lead.
			</p>
			<p>
				In this role, my team and I grew our unit from a handful of volunteers to more than twenty,
				and successfully planned and delivered medical cover at numerous different events
				independently. This included Night-Time Economy medical provision in Southampton, providing
				out-of-hospital urgent and emergency care to service users overnight, preventing pressure on
				the statutory ambulance service and hospitals.
			</p>
		</details>
		<details>
			<summary>
				<FaIcon icon={faMicrochip} />
				<strong>Technical Officer</strong>
				at the University of Southampton LGBTQ+ Society: November 2020 &mdash; July 2022
			</summary>
			<p>
				The UoS LGBTQ+ Society is the Students' Union society for students from gender and sexual
				minorities (GSMs). In my role as Technical Officer, I was responsible not only for digital
				infrastructure, but also for data protection, which is critically important in a society for
				GSM students.
			</p>
			<p>
				As well as designing a new authentication system in PHP and Discord bot in Go,
				I worked directly with the Students' Union's leadership to create appropriate technical and
				organisational measures to protect members' data, establish the Society's legal relationship
				with the Union, and investigate options to inject member data into insurance "pools" without
				the Union itself having a record of their membership of the Society.
			</p>
		</details>
	</section>

	<section class="qualifications">
		<h3>Qualifications <FaIcon icon={faBuildingColumns} /></h3>
		<div>
			<section class="flex-100">
				<h4>York St John University</h4>
				<section>
					<p>MSc Paramedic Science (Pre-Registration)</p>
					<p>Graduating July 2026 - on track for Distinction</p>
					<aside>
						<h6>Including level 7 modules in</h6>
						<ul>
							<li>Paramedic Research in Action</li>
							<li>Urgent and Primary Care for Paramedic Practice</li>
							<li>Concepts of Physical and Mental Health in Paramedic Practice</li>
						</ul>
					</aside>
				</section>
			</section>
			<section class="flex-50">
				<h4>University of Southampton</h4>
				<section>
					<p>BSc Computer Science</p>
					<p>First Class Honours</p>
					<aside>
						<h6>Optional modules</h6>
						<ul>
							<li>Principles of Cyber Security</li>
							<li>Web and Cloud Based Security</li>
							<li>Engineering Management and Law</li>
							<li>Advanced Databases</li>
							<li>Advanced Computer Networks</li>
						</ul>
					</aside>
				</section>
			</section>
			<section class="flex-30">
				<h4>St John Ambulance</h4>
				<section>
					<p>Emergency Ambulance Crew</p>
				</section>
				<section>
					<p>Cycle Responder</p>
				</section>
				<section>
					<p>Event Manager</p>
				</section>
			</section>
		</div>
	</section>
	<section class="references">
		<h3><FaIcon icon={faMessage} /> References</h3>
		<p>Available upon request.</p>
	</section>
</div>
<aside class="end">
	Convinced? <a href="mailto:cv@cpf.sh">Pop me an email.</a>
</aside>

<style lang="scss">
	@use 'sass:color';

	.intro {
		text-align: center;
	}

	.cv > section {
		padding-bottom: 1em;
		border: 0.3em dashed #353535;

		border-left: none;
		border-right: none;

		@include vars.light-mode {
			border-color: vars.$primary_dark;
		}

		h3 {
			@include vars.light-mode {
				color: vars.$primary_dark;
			}

			:global(svg) {
				margin-right: 0.3em;
			}
		}

		&:not(:first-child) {
			border-top: none;
		}

		&:nth-child(2n) {
			text-align: right;

			& > h3::after {
				display: inline;
			}

			& > h3::before {
				display: none;
			}

			details {
				text-align: left;
				summary {
					text-align: right;
				}
			}
		}

		&.qualifications div {
			text-align: left;
			display: flex;
			flex-wrap: wrap;

			// Handle our borders for light theme here
			@include vars.light-mode {
				border-bottom: 2px solid vars.$primary_dark;
				border-right: 2px solid vars.$primary_dark;
				& > section {
					border-top: 2px solid vars.$primary_dark;
					border-left: 2px solid vars.$primary_dark;
					margin-top: -2px;
					margin-left: -2px;

					h4 {
						color: vars.$primary_dark;
					}
				}
			}

			& > section {
				display: inline-block;

				padding: 2em;

				// Light theme borders are dealt with earlier
				@include vars.dark-mode {
					background-color: color.adjust(vars.$primary, $lightness: -30%);

					&:nth-of-type(3n-1) {
						background-color: color.adjust(vars.$primary, $lightness: -40%);
					}
					&:nth-of-type(3n) {
						background-color: color.adjust(vars.$primary, $lightness: -50%);
					}
				}

				aside {
					h6 {
						margin: 0;
						font-size: 1em;
					}
					li {
						font-size: 0.9em;
					}
				}
			}
		}
	}

	details {
		background: #252525;
		border: 2px solid #232323;
		padding: 0 2em;

		&:not(:first-of-type) {
			border-top: none;
		}

		@include vars.light-mode {
			background: #f4f4f4;
			border: 1px solid vars.$primary-dark;
		}

		&[open] {
			background: #303030;

			@include vars.light-mode {
				background: #e8e8e8;
			}
		}

		summary {
			margin-left: -2em;
			padding: 2em;
			width: 100%;

			transition: background-color 0.2s ease-in-out;

			&:hover {
				background-color: rgba(vars.$primary, 0.5);
				cursor: pointer;
			}

			:global(svg) {
				margin-right: 0.2em;
			}
		}
	}

	.end {
		text-align: center;
		padding: 1em 0;
	}
</style>
